import type { LayoutNode } from '@vedalib/editor'
import { getNodeName, isComplexElement, isShellNode } from '@vedalib/editor'
import { rtValueToText } from '@vedalib/rich-text'

import type { Block } from 'services/Store/Project/types'
import { notEmpty } from 'utils/notEmpty'

import { getElementByNode, getNode, getRootNode } from './block'

export const getAllTextInBlock = ({
  block,
  separator = ' ',
}: {
  block?: Block | null
  separator?: string
}) => {
  const elementsAll = block?.elements || {}
  const elementIds = Object.keys(elementsAll)
  const preparedElementsId = elementIds.filter((elId) => elementsAll[elId]?.type === 'richText')
  return preparedElementsId
    .reduce<
      string[]
    >((acc, id) => [...acc, rtValueToText((block?.elements[id]?.value as string) || '')], [])
    .join(separator)
}

interface IHierarchyNode {
  id: string
  type: string
  elementId?: string
  children: IHierarchyNode[]
  mode?: string
  name?: string
  entity: string
  value?: unknown
}

export const transformBlockToHierarchy = (block: Block): IHierarchyNode => {
  const processNode = (nodeId: string): IHierarchyNode | null => {
    const node = getNode(block, nodeId)

    if (!node) {
      return null
    }

    if (isShellNode(node)) {
      const element = getElementByNode(block, node)

      if (element) {
        if (isComplexElement(element)) {
          const children = element.value.items
            .map((item: { value: string }) => processNode(item.value))
            .filter(notEmpty)

          return {
            id: element.id,
            type: element.type,
            elementId: element.id,
            name: getNodeName(node),
            children,
            entity: 'element',
            value: element.value,
          }
        }

        return {
          id: element.id,
          type: element.type,
          elementId: element.id,
          name: getNodeName(node),
          children: [],
          entity: 'element',
          value: element.value,
        }
      }
    }

    const children = node.children?.map(processNode).filter(notEmpty) || []

    return {
      id: node.id,
      type: node.type,
      name: getNodeName(node),
      children,
      entity: 'node',
    }
  }

  const rootNode = getRootNode(block)

  return {
    id: rootNode.id,
    type: rootNode.type,
    name: getNodeName(rootNode),
    children: rootNode.children?.map(processNode).filter(notEmpty) || [],
    entity: 'node',
  }
}

interface IBlockElement {
  id?: string
  type?: string
  name?: string
  elements?: IBlockElement[]
  mode?: string
  entity?: string
  value?: unknown
}

interface IBlock {
  uuid?: string
  mode?: string
  name?: string
  elements?: IBlockElement[]
  type?: string
  entity?: string
}

const flattenNode = (
  node: IHierarchyNode,
  processElement: (node: IHierarchyNode) => IBlockElement[],
): IBlockElement[] => {
  const elements = node.children?.flatMap(processElement)
  if (['row', 'column'].includes(node.type)) {
    return elements
  }

  return [
    {
      id: node.id,
      type: node.type,
      name: getNodeName(node as unknown as LayoutNode),
      entity: node.entity,
      ...(elements.length && { elements }),
      value: node.value,
    },
  ]
}
export const transformToBlock = (data: IHierarchyNode): IBlockElement[] => {
  const processElement = (node: IHierarchyNode): IBlockElement[] =>
    flattenNode(node, processElement)

  return data.children?.flatMap(processElement)
}
export const transformBlockToNormalizedStructure = (block: Block): IBlock => {
  const processNode = (nodeId: string): IBlockElement[] => {
    const node = getNode(block, nodeId)

    if (!node) {
      return []
    }

    if (isShellNode(node)) {
      const element = getElementByNode(block, node)

      if (element) {
        if (isComplexElement(element)) {
          const children = element.value.items
            .flatMap((item: { value: string }) => processNode(item.value))
            .filter(notEmpty)

          return children
        }

        return [
          {
            id: element.id,
            type: element.type,
            name: getNodeName(node),
            entity: 'element',
            value: element.value,
          },
        ]
      }
    }

    const children = node.children?.flatMap(processNode)

    if (['row', 'column'].includes(node.type)) {
      return children
    }

    return [
      {
        id: node.id,
        type: node.type,
        name: getNodeName(node),
        elements: children,
        entity: 'node',
      },
    ]
  }

  const rootNode = getRootNode(block)
  const topLevelElements = rootNode.children?.flatMap(processNode)

  return {
    uuid: rootNode.id,
    mode: 'view',
    name: getNodeName(rootNode),
    elements: topLevelElements,
    type: rootNode.type,
    entity: 'node',
  }
}
